import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IconCalendarCheck } from "@tabler/icons-react";
import BusinessSelector from "./business-selector";
import { openPayingCustomersModal } from "@/components/paying-customers-only-modal";
import { useCreateReservation } from "@/hooks/api-hooks";
import { RESERVATIONS_PATH } from "@/lib/navigation";
import { isTier1Tenant } from "@/lib/format-utils";
import { RESERVATION_STATUS } from "@turizi/shared";
import { Business, Person, Reservation } from "@turizi/shared";
import { localTenant, localUser } from "@/services/session-service";

export interface NewReservationComponentProps {
    opened: boolean;
    onClose: () => void;
    onSuccess?: (reservation: Reservation) => void;
    iconSize?: number;
    iconStroke?: number;
}

export function useCreateNewReservation() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const tenant = localTenant();
    const createReservation = useCreateReservation();

    const createNewReservation = (status: string, client: Business | Person, callback?: () => void) => {
        if (!isTier1Tenant()) {
            openPayingCustomersModal();
            return;
        }
        const reservationPayload: Partial<Reservation> = {
            status,
            seller_id: tenant?.businesses?.[0]?.id || null,
        };
        if (client.type === 'business') {
            reservationPayload.client_id_business = client.id;
        }
        createReservation.mutate(reservationPayload, {
            onSuccess: (data) => {
                if (data?.data) {
                    callback?.();
                    navigate(`${RESERVATIONS_PATH}/${data.data.id}`);
                } else {
                    alert(t('error_creating_reservation'));
                }
            },
            onError: () => {
                alert(t('error_creating_reservation'));
            }
        });
    };

    return {
        createNewReservation,
        isPending: createReservation.isPending,
    };
}

export default function NewReservationComponent({
    opened,
    onClose,
    onSuccess,
    iconSize = 18,
    iconStroke = 2,
}: NewReservationComponentProps): JSX.Element {
    const { t } = useTranslation();
    const { createNewReservation } = useCreateNewReservation();
    const currentUser = localUser();

    const allowedIds = currentUser?.permissions?.reservation_business_mode === 'selected'
        ? currentUser.permissions.allowed_reservation_business_ids || []
        : undefined;

    return (
        <BusinessSelector
            opened={opened}
            onClose={onClose}
            allowedIds={allowedIds}
            title={
                <div className="flex items-center gap-2 text-lg font-semibold">
                    <IconCalendarCheck size={iconSize} stroke={iconStroke} color={RESERVATION_STATUS.confirmed.color} />
                    {t('new_reservation')}
                </div>
            }
            onSelect={(client) => {
                createNewReservation(RESERVATION_STATUS.confirmed.name, client, () => {
                    onClose();
                    if (onSuccess) {
                        // callback if provided
                    }
                });
            }}
        />
    );
}
